import {
    AfterViewInit,
    Component,
    ElementRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DocumentEditItemBase } from '../edit-item-base';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OperationOverlaySmallService } from '@fi-shared/operation-overlay-small/operation-overlay-small.service';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { isEmpty } from '@fi-shared/utils/is-empty';
import { eventFindInPath } from '@fi-shared/utils';
import { ITemplatePlaceHolderControlContent } from '@fi-app/template/template';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { ImageMovieFileTypes } from '@fi-shared/fi-insert-media/fi-insert-media-consts';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';

@Component({
    selector: 'fi-document-edit-item-image-handler',
    template: `
        <button #triggerButton #menuTrigger="matMenuTrigger" [matMenuTriggerFor]="menu" (menuClosed)="close()"></button>
        <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="openInsertMediaDialog()">{{ 'file.replace' | fiAppText }}</button>
            <button mat-menu-item (click)="removeInsertedMedia()">{{ 'common.clear' | fiAppText }}</button>
        </mat-menu>
    `,
    styleUrls: ['./image-handler.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-edit-item-image-handler' },
    standalone: false,
})
export class DocumentEditImageHandlerComponent
    extends DocumentEditItemBase
    implements AfterViewInit, OnDestroy, OnInit
{
    @Input() mouseClickEvent: MouseEvent;
    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
    @ViewChild('triggerButton', { static: true }) triggerButton: ElementRef;
    currentContent: ITemplatePlaceHolderControlContent;
    onClose$ = new Subject<void>();
    onMediaBankFileChanged$ = new Subject<any>();
    onContentChange$ = new Subject<void>();

    constructor(
        injector: Injector,
        private operationOverlaySmallService: OperationOverlaySmallService,
        private insertMedia2Service: FiInsertMedia2Service,
        private mediaStorageService: FiMediaStorageService,
        private renderer2: Renderer2,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.onClose$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.close());
        this.onMediaBankFileChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(($event) => {
            this.mediaBankFileChangedEmitter.emit($event);
            this.contentChange.emit($event);
            this.close();
        });
        this.onContentChange$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(($event) => this.contentChange.emit($event));
    }

    ngAfterViewInit() {
        this.currentContent = this.item.model.Contents.find((content) => content.ContentLanguageId === this.languageId);
        if (this.isInsertMedia()) {
            this.openInsertMediaDialog();
        } else if (this.isFileInserted() || this.isAdditionFileInserted()) {
            this.openEditMediaMatMenu();
        }
    }

    openInsertMediaDialog() {
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.Document, {
                multiple: false,
                accept: getAcceptByFileType(ImageMovieFileTypes, undefined),
                fileTypes: ImageMovieFileTypes,
            })
            .onYesButtonClick((result: IInsertMediaResult) => {
                if (this.currentContent) {
                    this.onMediaBankFileChanged$.next({
                        newId: result.mediaBankFiles[0].Id,
                        oldId: this.getMediaBankFileId(),
                    });
                    this.templateLayoutHelper.setTemplatePlaceHolderImageContent(
                        this.currentContent,
                        this.selectedColumn,
                        result.mediaBankFileReferences[0],
                    );
                }
            })
            .afterClosed(() => this.close());
    }

    private openEditMediaMatMenu() {
        if (this.currentContent && this.mouseClickEvent) {
            // open matMenu in position where user clicked
            const target = this.mouseClickEvent.target as HTMLElement;
            const columnElement = eventFindInPath({ target }, '.column') as HTMLElement;
            if (columnElement) {
                const parentPadding = parseInt(
                    getComputedStyle(columnElement).getPropertyValue('padding').slice(0, -2),
                );

                this.renderer2.setStyle(this.triggerButton.nativeElement, 'position', 'absolute');
                this.renderer2.setStyle(
                    this.triggerButton.nativeElement,
                    'top',
                    this.mouseClickEvent.offsetY + parentPadding + 'px',
                );
                if (this.item.model.Settings.Columns === 2 && this.selectedColumn === 2) {
                    // (parentPadding * (this.item.model.Settings.Columns + 1) we need add padding left and right values
                    // of each element plus padding left value of the last element to get correct cursor position
                    this.renderer2.setStyle(
                        this.triggerButton.nativeElement,
                        'left',
                        this.mouseClickEvent.offsetX +
                            target.clientWidth +
                            parentPadding * (this.item.model.Settings.Columns + 1) +
                            'px',
                    );
                } else {
                    this.renderer2.setStyle(
                        this.triggerButton.nativeElement,
                        'left',
                        this.mouseClickEvent.offsetX + parentPadding + 'px',
                    );
                }
                this.renderer2.setStyle(this.triggerButton.nativeElement, 'visibility', 'hidden');
                this.trigger.openMenu();
            }
        }
    }

    removeInsertedMedia() {
        const oldMediaId = this.getMediaBankFileId();

        this.templateLayoutHelper.deleteTemplatePlaceHolderImageContent(this.currentContent, this.selectedColumn);
        this.onMediaBankFileChanged$.next({ newId: undefined, oldId: oldMediaId });
    }

    private getMediaBankFileId(): number {
        if (this.currentContent) {
            if (this.isFileInserted()) return this.currentContent.Value.File.MediaBankFileId;

            if (this.isAdditionFileInserted()) return this.currentContent.Value.additionalFile.MediaBankFileId;
        }
        return undefined;
    }

    private isInsertMedia(): boolean {
        return (
            this.currentContent &&
            ((isEmpty(this.currentContent.Value.File) && this.selectedColumn === 1) ||
                (isEmpty(this.currentContent.Value.additionalFile) && this.selectedColumn === 2))
        );
    }

    private isFileInserted(): boolean {
        return (
            this.currentContent &&
            !isEmpty(this.currentContent.Value.File) &&
            this.selectedColumn === 1 &&
            this.currentContent.Value.File.DisplayAs !== FiDisplayAs.DownloadableFile.Id
        );
    }

    private isAdditionFileInserted(): boolean {
        return (
            this.currentContent &&
            !isEmpty(this.currentContent.Value.additionalFile) &&
            this.selectedColumn === 2 &&
            this.currentContent.Value.additionalFile.DisplayAs !== FiDisplayAs.DownloadableFile.Id
        );
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
